/*
----------------------------------------------------------------------------
Usage:
----------------------------------------------------------------------------
import FormValidate from './modules/form-validate'
_('', 'validate').nodes().map((el) => new FormValidate(el))

----------------------------------------------------------------------------
Base attributes:
----------------------------------------------------------------------------
data-validate      - root attribute
*/

import { _ } from '../utils.js'

export default class audioPlayer {
    constructor(el) {
        this.el = el
        this.playButton = this.el.querySelector(_('audio-play').str())
        this.soundButton = this.el.querySelector(_('audio-sound').str())
        this.originalAudio = this.el.querySelector(
            _('original-audio', 'element').str(),
        )
        this.currentTimeDisplay = this.el.querySelector(
            _('current-time-display', 'element').str(),
        )
        this.durationBar = this.el.querySelector(
            _('audio-duration-bar', 'element').str(),
        )
        this.isPlaying = false
        this.soundOn = ''
        this.secondsCounted = null
        this.isScrubbed = false

        this.playButton.addEventListener('click', this.togglePlay)
        this.soundButton.addEventListener('click', this.toggleSound)
        this.durationBar.value = 0
        this.durationBar.addEventListener('input', this.scrubTime)
        this.updateDuration()
    }

    togglePlay = () => {
        this.playIcon = this.playButton.querySelector(
            _('play-icon', 'element').str(),
        )
        this.pauseIcon = this.playButton.querySelector(
            _('pause-icon', 'element').str(),
        )

        this.playCheck()
        if (this.isPlaying) {
            this.originalAudio.pause()
            this.playIcon.classList.remove('u-hidden')
            this.pauseIcon.classList.add('u-hidden')
            clearInterval(this.durationPlayed)
        } else {
            this.originalAudio.play()
            this.playIcon.classList.add('u-hidden')
            this.pauseIcon.classList.remove('u-hidden')
            this.durationPlayed = setInterval(this.updateCurrentTime, 1000)
        }
    }

    playCheck = () => {
        if (this.originalAudio.duration > 0 && !this.originalAudio.paused) {
            this.isPlaying = true
        } else {
            this.isPlaying = false
        }
    }

    toggleSound = () => {
        this.soundOn = this.soundOn ? false : true
        this.speakerIcon = this.el.querySelector(
            _('speaker-icon', 'element').str(),
        )
        this.mutedIcon = this.el.querySelector(_('muted-icon', 'element').str())

        if (this.soundOn) {
            this.originalAudio.volume = 0
            this.speakerIcon.classList.add('u-hidden')
            this.mutedIcon.classList.remove('u-hidden')
        } else {
            this.originalAudio.volume = 1
            this.speakerIcon.classList.remove('u-hidden')
            this.mutedIcon.classList.add('u-hidden')
        }
    }

    updateDuration = () => {
        this.originalAudio.addEventListener('loadedmetadata', () => {
            // Find and format duration
            this.totalDurationDisplay = this.el.querySelector(
                _('total-duration-display', 'element').str(),
            )
            this.durationMax = Math.floor(this.originalAudio.duration)
            this.totalDurationHours = Math.floor(this.durationMax / 60 / 60)
            this.totalDurationMins =
                Math.floor(this.durationMax / 60) - this.totalDurationHours * 60
            this.totalDurationSecs =
                Math.floor(this.originalAudio.duration) % 60
            this.formattedDuration =
                this.totalDurationHours.toString().padStart(2, '0') +
                ':' +
                this.totalDurationMins.toString().padStart(2, '0') +
                ':' +
                this.totalDurationSecs.toString().padStart(2, '0')

            this.durationBar.setAttribute('max', this.durationMax)
            this.totalDurationDisplay.textContent = this.formattedDuration
        })
    }

    updateCurrentTime = () => {
        let newTimeValue = null

        this.playCheck()
        if (!this.isScrubbed) {
            newTimeValue = this.secondsCounted += 1
            this.durationBar.value = this.secondsCounted
        } else {
            this.durationBar.value = Math.floor(this.originalAudio.currentTime)
            newTimeValue = Math.floor(this.originalAudio.currentTime)
        }
        // Format time played as 00:00:00 for text display
        this.hoursPlayed = Math.floor(newTimeValue / 60 / 60)
        this.minsPlayed = Math.floor(newTimeValue / 60) - this.hoursPlayed * 60
        this.secsPlayed = Math.floor(newTimeValue) % 60
        this.formattedPlayed =
            this.hoursPlayed.toString().padStart(2, '0') +
            ':' +
            this.minsPlayed.toString().padStart(2, '0') +
            ':' +
            this.secsPlayed.toString().padStart(2, '0')

        this.currentTimeDisplay.textContent = this.formattedPlayed
    }

    scrubTime = () => {
        const durationValue = this.durationBar.value
        this.isScrubbed = true

        // Format time played as 00:00:00 for text display
        this.hoursScrubbed = Math.floor(durationValue / 60 / 60)
        this.minsScrubbed =
            Math.floor(durationValue / 60) - this.hoursScrubbed * 60
        this.secsScrubbed = Math.floor(durationValue) % 60
        this.formattedScrubbed =
            this.hoursScrubbed.toString().padStart(2, '0') +
            ':' +
            this.minsScrubbed.toString().padStart(2, '0') +
            ':' +
            this.secsScrubbed.toString().padStart(2, '0')

        this.currentTimeDisplay.textContent = this.formattedScrubbed

        // Set original audio to new time selected
        this.originalAudio.currentTime = durationValue
    }
}
